import {
  FOC_TOUR_LABEL_TRANSITION,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import {
  FOC_REQUIRED_DISPOSITION,
  IOC_CARRYOVER_DISPOSITION,
} from '../orgWorkspaceTour/orgWorkspaceTourSteps';
import { CorporateViewTabs } from '../hubNavTabs';
import {
  focTransitionDemoHref,
  focTransitionIrHref,
  focTransitionPlanningHref,
  preferredFocTransitionId,
  type FocTransitionIncident,
} from './focTransitionDemo';

export const FOC_TRANSITION_TOUR_PARAM = 'focTransitionTour';

/** Survives IR → Planning P redirect after this tour escalates the demo. */
export const FOC_TRANSITION_TOUR_CONTINUE_KEY = 'focTransitionTourContinue';

/** Hub entry; host resolves Transition demo and opens IR or Planning P. */
export const FOC_TRANSITION_TOUR_HREF = `/hub?openTab=${CorporateViewTabs.Incidents}&${FOC_TRANSITION_TOUR_PARAM}=1`;

export type FocTransitionTourSurface = 'initialResponse' | 'planning';

export type FocTransitionTourStepId =
  | 'sectionIntro'
  | 'trn001'
  | 'trn001a'
  | 'trn001b'
  | 'trn002'
  | 'trn003'
  | 'trn003a'
  | 'trn004'
  | 'user001'
  | 'user002'
  | 'user006'
  | 'trn005'
  | 'trn005a'
  | 'trn005b'
  | 'trn005c'
  | 'focFeedback';

export type FocTransitionTourStepDef = {
  id: FocTransitionTourStepId;
  requirementId?: string;
  title: string;
  disposition?: string;
  description: string;
  bullets?: string[];
  tour?: string;
  surface?: FocTransitionTourSurface;
  /** Planning forms dropdown widget name when preparing this step. */
  widget?: string;
  /** When true, Next escalates the workspace and navigates to Planning P. */
  escalateOnNext?: boolean;
};

type RequirementInput = Omit<
  FocTransitionTourStepDef,
  'title' | 'requirementId'
> & {
  requirementId: string;
  name: string;
};

function requirement({
  requirementId,
  name,
  ...rest
}: RequirementInput): FocTransitionTourStepDef {
  return {
    ...rest,
    requirementId,
    title: `${requirementId} — ${name}`,
  };
}

/**
 * Walk for 9.1 Transition: escalate from New ICS 201 into Planning P stem.
 */
export const FOC_TRANSITION_TOUR_STEPS: FocTransitionTourStepDef[] = [
  {
    id: 'sectionIntro',
    surface: 'initialResponse',
    tour: 'incident-ir-forms',
    title: '9.1 Transition to Planning P',
    description:
      'Move from ICS 201 initial response into deliberate Planning P operations. This walk uses a seeded Transition demo, escalates with your Advance Operational Period permission, then covers the one-time Planning P stem on phase IC/UC Objectives.',
  },
  requirement({
    id: 'trn001',
    requirementId: 'FOC-TRN-001',
    name: 'Visible Commence Planning P Action',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'initialResponse',
    tour: 'incident-escalate',
    description:
      'PRATUS provides a prominent Escalate Response action in the initial-response workspace header to commence Planning P / escalate to deliberate incident-management operations. Escalating requires the Advance Operational Period permission (incident:advanceOperationalPeriod).',
  }),
  requirement({
    id: 'trn001a',
    requirementId: 'FOC-TRN-001a',
    name: 'Direct Workflow Entry',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'initialResponse',
    tour: 'incident-escalate-workflow',
    escalateOnNext: true,
    description:
      'After authorized confirmation and planning-workflow selection, Next escalates this workspace and opens Planning P directly — not generic incident settings. If escalate fails (for example missing permission), the tour stays here so you can resolve access.',
  }),
  requirement({
    id: 'trn001b',
    requirementId: 'FOC-TRN-001b',
    name: 'One-Time Planning P Stem',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'planning',
    tour: 'planning-stepper',
    description:
      'The initial transition from ICS 201 into sustained incident management is a one-time Planning P stem (phase IC/UC Objectives), distinct from the later recurring Planning P cycle across operational periods.',
  }),
  requirement({
    id: 'trn002',
    requirementId: 'FOC-TRN-002',
    name: 'Initial IC/Unified Command Briefing',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'planning',
    tour: 'planning-forms',
    description:
      'Planning P phase 0 uses ICS 201 and approved incident information as the source for current situation, map, objectives, actions, organization, facilities, resources, weather and constraints, safety concerns, information requirements, and other initial context.',
  }),
  requirement({
    id: 'trn003',
    requirementId: 'FOC-TRN-003',
    name: 'Initial Unified Command Meeting',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'planning',
    tour: 'planning-agenda',
    description:
      'The IC/UC Objectives agenda captures and preserves authorized transition decisions — command authority, UC structure, jurisdiction, incident name, AOR, ICP, operational-period parameters, priorities and objectives, IMT roles, meeting cadence, information flow, resource ordering, OPSEC, and Critical Information Requirements — before the recurring ICS 202 cycle.',
  }),
  requirement({
    id: 'trn003a',
    requirementId: 'FOC-TRN-003a',
    name: 'Optional Bypass',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'planning',
    tour: 'planning-complete-phase',
    description:
      'Authorized users can deliberately advance past transition work with Complete Phase. The action is visible, intentional, and recorded on the operational period. Skipped agenda work can still be revisited when operationally appropriate.',
  }),
  requirement({
    id: 'trn004',
    requirementId: 'FOC-TRN-004',
    name: 'Transition Continuity',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'planning',
    tour: 'planning-form-card',
    widget: 'incidentObjectives',
    description:
      'Relevant ICS 201 objectives, actions, organization, map, resources, safety information, and initial decisions remain available in Planning P so responders do not re-enter transition products from scratch.',
  }),
  requirement({
    id: 'user001',
    requirementId: 'US-ER001',
    name: 'Auto-Populate Incident Objectives, ICS 201 to ICS 202',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'planning',
    tour: 'ics202-card',
    widget: 'incidentObjectives',
    description:
      'Incident objectives defined on the ICS 201 during initial response continue into the ICS 202 (Incident Objectives) after escalation — the same incident-scoped records — so strategic continuity is maintained without re-entry.',
  }),
  requirement({
    id: 'user002',
    requirementId: 'US-ER002',
    name: 'Auto-Populate Open Actions, ICS 201 to ICS 233',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'planning',
    tour: 'ics233-card',
    widget: 'openActionsTracker',
    description:
      'Current/Ongoing and Planned actions from the ICS 201 continue into the ICS 233 (Incident Open Action Tracker) after escalation so pending tactical tasks are not lost when the IMT forms up.',
  }),
  requirement({
    id: 'user006',
    requirementId: 'US-ER006',
    name: 'Manage Incident Objectives, ICS 202',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'planning',
    tour: 'ics202-card',
    widget: 'incidentObjectives',
    description:
      'Authorized users can view and edit ICS 202 fields — including operational period, general situational awareness, and command emphasis — so the IAP reflects current leadership direction.',
  }),
  requirement({
    id: 'trn005',
    requirementId: 'FOC-TRN-005',
    name: 'Initial Objectives Meeting and ICS 202',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'planning',
    tour: 'planning-stepper',
    description:
      'The Initial Objectives Meeting is the first recurring-planning-cycle event after the Initial IC/UC Briefing and Initial Unified Command Meeting work framed in this stem. On PRATUS it is the IC/UC Objectives phase with the ICS 202 product.',
  }),
  requirement({
    id: 'trn005a',
    requirementId: 'FOC-TRN-005a',
    name: 'Operational-Period Reset',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'planning',
    tour: 'planning-show-agenda',
    description:
      'The Objectives Meeting enables Incident Command, Unified Command, and authorized participants to establish or revise objectives, operational-period parameters, command emphasis, situational awareness, CIRs, safety linkage, and direction for the next operational period.',
  }),
  requirement({
    id: 'trn005b',
    requirementId: 'FOC-TRN-005b',
    name: 'Source-Faithful ICS 202',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'planning',
    tour: 'ics202-export',
    widget: 'incidentObjectives',
    description:
      'PRATUS generates, supports review of, approves, and exports a source-faithful ICS 202 containing authorized operational-period, situational awareness, command emphasis, objective, CIR, and safety-message information.',
  }),
  requirement({
    id: 'trn005c',
    requirementId: 'FOC-TRN-005c',
    name: 'AI-Assisted ICS 202 Drafting',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'planning',
    description:
      'PRATUS may assist authorized users with drafting ICS 202 content from authorized incident information and approved source materials. The authoring interface identifies source provenance and AI assistance. Raw AI citation markers or source numbers do not automatically appear on an official final ICS form. This walkthrough does not run AI generation.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_TRANSITION),
  },
];

export function focTransitionTourNavVisible(enabled: boolean): boolean {
  return enabled;
}

export function resolveFocTransitionHref(
  incidents: readonly FocTransitionIncident[] = []
): string | null {
  const id = preferredFocTransitionId(incidents);
  if (!id) return null;
  const demo = incidents.find((incident) => String(incident.id) === id);
  return focTransitionDemoHref(id, Boolean(demo?.escalated_at));
}

export function focTransitionTourStepIds(): FocTransitionTourStepId[] {
  return FOC_TRANSITION_TOUR_STEPS.map((step) => step.id);
}

export function focTransitionTourStepTitles(): string[] {
  return FOC_TRANSITION_TOUR_STEPS.map((step) => step.title);
}

export function focTransitionTourPopoverHtml(
  step: FocTransitionTourStepDef
): string {
  const parts: string[] = [];
  if (step.disposition) {
    parts.push(
      `<p><strong>Disposition:</strong> ${escapeHtml(step.disposition)}</p>`
    );
  }
  parts.push(`<p>${escapeHtml(step.description)}</p>`);
  if (step.bullets?.length) {
    parts.push(
      `<ul>${step.bullets
        .map((bullet) => `<li>${escapeHtml(bullet)}</li>`)
        .join('')}</ul>`
    );
  }
  return parts.join('');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export { focTransitionIrHref, focTransitionPlanningHref };
